import { processText } from '@aytm/app/stats/page/components/lib/utils'

import {
  MAX_SUM,
  calcPositionSum,
  calcNACoefficient,
  calcImportance
} from '@aytm/app/stats/page/question-views/cjc/lib/calc'

// styles
import style from '@aytm/app/stats/page/question-views/cjc/components/market-simulator/styles/MarketSimulator.module.scss'

// Summary/lite view importance table (replaces the legacy Backbone ItemsListView). Each
// attribute row shows its importance %, ordered high-to-low; importance is inherently a
// percentage so there is no amount column. Mirrors calcLiteItemsListModel.
export const renderImportanceTableProps = (answer, batches, persona) => {
  const hasCluster = !persona || answer?.clusters?.[persona - 1] != null

  const rows = (batches || [])
    .filter(b => b && !b.na)
    .map(b => {
      const raw = hasCluster ? calcImportance(answer, b.position, persona) : 0
      return { position: b.position, text: b.text, percents: Math.max(0, Math.min(100, 100 * (raw || 0))) }
    })
    .filter(a => !isNaN(a.percents))
    .sort((a, b) => b.percents - a.percents)

  return {
    cols: [
      { type: 'title', title: 'Attributes' },
      { type: 'progress', title: 'Importance', width: 182 }
    ],
    rows: rows.map(a => ({
      position: a.position,
      cells: [{ title: `${a.position}. ${processText(a.text)}` }, { percents: a.percents }]
    }))
  }
}

export const renderMarketSimulatorProps = (answer, mode, persona, marketSimulatorPackages, marketSimulatorNA) => {
  const config = answer?.config || { v: 1 }

  if (1 == config.v) {
    const arr = marketSimulatorPackages.map(p => Math.exp(Math.min(MAX_SUM, calcPositionSum(mode, answer, persona, p))))
    let sum = arr.reduce((acc, cur) => acc + cur, 0)

    marketSimulatorNA && (sum += Math.exp(calcNACoefficient(answer, persona)))

    return {
      packages: marketSimulatorPackages,
      percents: arr.map(p => (sum ? (100 * p) / sum : 0)),
      naPercent: sum ? (100 * Math.exp(calcNACoefficient(answer, persona))) / sum : 0
    }
  } else {
    let weights = null
    let weightsSum = 0

    const respondents =
      persona && answer.clusters && answer.clusters[persona - 1] ? answer.clusters[persona - 1].respondents : null

    if (answer.weights) {
      weights = respondents
        ? answer.individual_raw_coefficients_adjusted.reduce((acc, cur, i) => {
            if (respondents.includes(cur[0])) {
              const weight = answer.weights[i]
              acc.push(weight)
            }
            return acc
          }, [])
        : answer.weights
      weightsSum = weights.reduce((acc, cur) => acc + cur, 0)
    }

    const coeffs = marketSimulatorPackages
      .map(p =>
        answer.individual_raw_coefficients_adjusted
          .filter(r => null === respondents || respondents.includes(r[0]))
          .map(r => {
            const coeff = []
            for (const [pi, bi] of p.entries()) {
              const start = (answer.featureLengths || []).slice(0, pi).reduce((acc, cur) => acc + cur, 0)
              const index = start + bi
              coeff.push(r[index])
            }
            return coeff.reduce((acc, cur) => acc + cur, 0)
          })
          .map(v => Math.exp(v))
      )
      .reduce((acc, cur) => {
        cur.forEach((v, j) => {
          acc[j] ||= []
          acc[j].push(v)
        })
        return acc
      }, [])
      .map((v, i) => {
        // the collector may omit the list; the sibling aggregate read falls to the same sentinel
        if (marketSimulatorNA) v.push(Math.exp(answer.individualNACoeffRaw?.[i]?.[1] ?? -999))
        return v
      })
    const percents = coeffs
      .map(c => c.map((v, _, arr) => v / arr.reduce((acc, cur) => acc + cur, 0)))
      .reduce((acc, cur, j) => {
        cur.forEach((v, i) => {
          acc[i] ||= 0
          acc[i] += null !== weights ? weights[j] * v : v
        })
        return acc
      }, [])
      .map(sum => (null !== weights ? (100 * sum) / weightsSum : (100 * sum) / coeffs.length))

    return {
      packages: marketSimulatorPackages,
      percents: 0 < percents.length && marketSimulatorNA ? percents.slice(0, -1) : percents,
      naPercent: 0 < percents.length && marketSimulatorNA ? percents[percents.length - 1] : 0
    }
  }
}

export const renderMarketSimulatorTableProps = (
  batches,
  packages,
  percents,
  deleteMarketSimulatorPackage,
  setMarketSimulatorPackage
) => {
  const filteredBatches = batches.filter(b => 0 < b?.answerVariants.length)

  return {
    classNames: ['market-simulator'],
    cols: [
      {
        type: 'value',
        title: '',
        width: 40
      },
      ...filteredBatches.map(b => {
        return {
          type: 'value',
          title: processText(b.text),
          width: `calc((100% - 110px) / ${filteredBatches.length})`
        }
      }),
      { type: 'value', title: '', width: 70 }
    ],
    rows: packages
      .filter(p => null !== p)
      .map((p, pi) => {
        return {
          cells: [
            {
              value: `<span class="${style.delete}"><i class="icon-aytmfd-x-v2"></i>`,
              onClick: () => deleteMarketSimulatorPackage(pi)
            },
            ...filteredBatches.map((b, i) => {
              return {
                value: processText((b.answerVariants[p[i] - 1] && b.answerVariants[p[i] - 1].text) || p),
                onClick: () => setMarketSimulatorPackage(pi)
              }
            }),
            { value: `<b>${percents[pi].toFixed(1)}</b>%`, onClick: () => setMarketSimulatorPackage(pi) }
          ],
          position: pi + 1
        }
      })
  }
}
